/**
 * Storage and identity for analytics.
 *
 * Everything tracking reads or writes goes through here, and nothing here can throw.
 * sessionStorage/localStorage throw outright in some sandboxed iframes and in-app
 * browsers, and tracking runs inside click handlers ("Чатлах", "CV илгээх") — an
 * exception escaping from an analytics call used to stop the button itself. When storage
 * is unavailable the value lives in memory instead, so it still holds for this page load.
 *
 * No React, no API client: tracking.js, sourceDetection.js and jobView.js all import
 * this, and keeping it dependency-free keeps those three free of import cycles.
 */

const memory = {};

const read = (area, key) => {
  try {
    const value = window[area].getItem(key);
    if (value !== null) return value;
  } catch (error) {
    // storage blocked: fall through to the in-memory copy
  }
  return Object.prototype.hasOwnProperty.call(memory, `${area}:${key}`)
    ? memory[`${area}:${key}`]
    : null;
};

const write = (area, key, value) => {
  memory[`${area}:${key}`] = value;
  try {
    window[area].setItem(key, value);
  } catch (error) {
    // storage blocked or full: the in-memory copy above stands in
  }
};

const remove = (area, key) => {
  delete memory[`${area}:${key}`];
  try {
    window[area].removeItem(key);
  } catch (error) {
    // ignore
  }
};

export const sessionGet = (key) => read('sessionStorage', key);
export const sessionSet = (key, value) => write('sessionStorage', key, value);
export const sessionRemove = (key) => remove('sessionStorage', key);
export const localGet = (key) => read('localStorage', key);
export const localSet = (key, value) => write('localStorage', key, value);

/** JSON helpers — a corrupt value reads as `fallback` rather than throwing. */
export const sessionGetJson = (key, fallback) => {
  const raw = sessionGet(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
};
export const sessionSetJson = (key, value) => {
  try {
    sessionSet(key, JSON.stringify(value));
  } catch (error) {
    // unserialisable value: nothing worth surfacing
  }
};

export const randomId = () =>
  `${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;

// --- Session -----------------------------------------------------------------------
//
// One id per browser tab, from the first page load until the tab is closed. The key is
// the one hrSystemServer's /job-view/record dedupe has always been fed, so existing rows
// and new rows share the same notion of a session.

const VIEW_SESSION_KEY = 'diil_view_session';

export const getViewSessionId = () => {
  let sessionId = sessionGet(VIEW_SESSION_KEY);
  if (!sessionId) {
    sessionId = randomId();
    sessionSet(VIEW_SESSION_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Start a new session in this tab — used when a returning tab arrives from a new
 * campaign (see sourceDetection.initSourceTracking). Everything keyed by session id
 * (viewed-job dedupe, the visit_start flag) resets with it.
 */
export const resetViewSession = () => {
  const sessionId = randomId();
  sessionSet(VIEW_SESSION_KEY, sessionId);
  return sessionId;
};

// --- Visitor -----------------------------------------------------------------------
//
// One id per browser, kept across visits. The only way to tell a returning anonymous
// visitor from a new one, and to join what someone did before logging in to what they
// did after.

const VISITOR_KEY = 'diil_visitor_id';
const VISIT_COUNT_KEY = 'diil_visit_count';
const LAST_VISIT_KEY = 'diil_last_visit_at';

export const getVisitorId = () => {
  let visitorId = localGet(VISITOR_KEY);
  if (!visitorId) {
    visitorId = randomId();
    localSet(VISITOR_KEY, visitorId);
  }
  return visitorId;
};

/**
 * Count this visit and report how this browser has been here before. Call once per
 * session (visit_start does).
 * @returns {{ visitNumber: number, daysSinceLastVisit: number|null }}
 */
export const registerVisit = () => {
  const previousCount = parseInt(localGet(VISIT_COUNT_KEY) || '0', 10) || 0;
  const lastVisitAt = parseInt(localGet(LAST_VISIT_KEY) || '', 10);
  const now = Date.now();
  localSet(VISIT_COUNT_KEY, String(previousCount + 1));
  localSet(LAST_VISIT_KEY, String(now));
  return {
    visitNumber: previousCount + 1,
    daysSinceLastVisit: Number.isFinite(lastVisitAt) && lastVisitAt > 0
      ? Math.floor((now - lastVisitAt) / 86400000)
      : null,
  };
};
